import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {MatrixClient, MatrixEvent, Room, RoomMember, RoomMemberEvent, Direction, EventType} from 'matrix-js-sdk';
import {ChatState, matrixService, SearchOptions} from '../services/matrixService';
import {ChatLoginResponse} from "../services/chatServiceTypes.ts";
import {formatDateDayMonth, weekDays} from "../utilis/timeUtilis.ts";
import {browserNotificationService} from "../services/browserNotificationService.ts";
import {notificationBatchingService} from "../services/notificationBatchingService.ts";
import {useDispatch, useSelector} from "react-redux";
import {showToast} from "../store/features/Notifications/notificationsSlice.tsx";
import {browserNavigationService} from "../services/NavigationService.ts";
import notification  from '../assets/sounds/notification.mp3';
import {TabTitleAnimator} from "../utilis/tabAnimator.ts";
import {FaviconBadge} from "../utilis/faviconBadge.ts";
import {logDev} from "../utils/loggerHelpers.ts";
import logger from "../services/logger";
import {resetDirectRoomInFlight} from "../pages/Chat/directRoomLookup";
import {record, requestReference, resultBelongsToCurrentRequest} from '../utils/aiRequestProtocol';
import {isBotUser, isServiceParticipant} from "../utils/aiBotUtils.ts";
import {formatMessagePreview} from "../utils/messageFormatting.ts";
import {isMessageEdit} from "../utils/messageEdits.ts";
import {
    formatLastEvent,
    getRoomSortTimestamp,
    isRelevantTimelineEvent,
    isRoomMemberJoinEvent,
    isRtcSignalEvent,
    isSummaryMessageContent,
    isTranscriptionMessageContent,
    LastEventInfo,
    deduplicateTimelineEvents,
    keepMembershipTransitions,
} from "../utilis/chatUtils.ts";
import {type CallTimelineEntry, isCallInviteEvent, isCallTimelineEvent} from "../utilis/callTimeline.ts";
import {isRtcNotificationEvent} from "../utilis/rtcNotification.ts";
import {isOneToOneCallRoom} from "../features/groupCalls/services/callRoomShape.ts";
import {ringOutcomes} from "../features/groupCalls/services/ringOutcomes.ts";
import {
    type ChatNotificationMute,
    type ChatNotificationMuteDurationHours,
    isChatNotificationMuteActive,
    isRoomChatNotificationMuted,
    removeActiveRoomChatNotificationMute,
    setActiveRoomChatNotificationMute,
    useGetActiveChatNotificationMutesQuery,
    useMuteChatRoomMutation,
    useUnmuteChatRoomMutation,
} from '../services/chatNotificationMuteService.ts';
import {selectTenant, selectUserId} from '../store/features/Auth/authSlice.ts';

const MAX_AI_NOTIFICATION_RESULTS = 512;

export interface OtherUser {
    userId: string;
    displayName: string;
    avatarUrl: string;
}

export interface RoomWithLastMessage {
    room: Room | null;
    roomId: string;
    roomName?: string;
    otherUsers: OtherUser[];
    unreadCount: number;
    isLocal?: boolean;
    isDirect?: boolean;
    localCreatedAt?: number;
    lastMessage: LastEventInfo | null;
}

export interface UnreadSummary {
    total: number;
    byRoom: Array<{ roomId: string; roomName: string; count: number }>;
}

export const summarizeUnreadRooms = (rows: RoomWithLastMessage[]): UnreadSummary => {
    const byRoom: UnreadSummary['byRoom'] = [];
    let total = 0;
    for (const row of rows) {
        if (row.unreadCount <= 0) continue;
        total += row.unreadCount;
        byRoom.push({
            roomId: row.roomId,
            roomName: row.room?.name || row.roomName || 'Unnamed Room',
            count: row.unreadCount
        });
    }
    return {total, byRoom};
};

export const useMatrix = () => {
    const dispatch = useDispatch();
    const [state, setState] = useState<ChatState>(matrixService.getState());
    const [messages, setMessages] = useState<Map<string, MatrixEvent[]>>(new Map());
    const [roomsWithLastMessages, setRoomsWithLastMessages] = useState<RoomWithLastMessage[]>([]);
    const [notificationPermission, setNotificationPermission] = useState<string>('default');
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const isInitialSyncRef = useRef<boolean>(true);
    const notifiedAiResultsRef = useRef(new Set<string>());

    const tabAnimator = new TabTitleAnimator()
    const faviconBadge = new FaviconBadge();

    const notificationAudioRef = new Audio(notification);
    notificationAudioRef.volume = 0.3;

    const findParentRoom = useCallback((): Room | null => {
        const rooms = matrixService.getRooms();
        return rooms.find(room => room.isSpaceRoom()) || null;
    }, []);

    const getLastMessageForRoom = useCallback((room: Room): LastEventInfo | null => {
        try {
            const timeline = room.getLiveTimeline();
            if (!timeline) return null;

            const events = timeline.getEvents();
            const relevantEvents = events.filter(isRelevantTimelineEvent);

            if (relevantEvents.length === 0) return null;

            return formatLastEvent(relevantEvents[relevantEvents.length - 1], room, {
                isOneToOneRoom: candidate => isOneToOneCallRoom(candidate, matrixService.isDirectMessageRoom(candidate)),
            });
        } catch (error) {
            console.error('Error getting last message for room:', room.roomId, error);
            return null;
        }
    }, []);

    const updateRoomsWithLastMessages = useCallback(() => {
        matrixService.reconcileLocalRoomsWithRealRooms();

        const currentUserId = matrixService.getState().currentUserId;
        const localRooms = matrixService.getLocalRooms ? matrixService.getLocalRooms() : [];

        // Get real room IDs that are mapped from local rooms — hide them by ID
        const hiddenRealRoomIds = matrixService.getRealRoomIdsForLocalRooms();
        const unmappedLocalRooms = localRooms.filter(localRoom =>
            !matrixService.getMappedRealRoomId(localRoom.localId)
        );

        const normalizeName = (name?: string) => (name || '').trim();
        const isResolvedRoomName = (name?: string) => {
            const normalized = normalizeName(name);
            return !!normalized && normalized !== 'Empty room' && !normalized.includes('chat.chte.ch');
        };
        const getComparableOtherUserIds = (ids: string[]) =>
            ids.filter(id => !!id && !isServiceParticipant(id)).sort();
        const getRoomCreateEvent = (room: Room) => room.currentState.getStateEvents('m.room.create', '');
        const isRoomCreatedByCurrentUser = (room: Room) =>
            getRoomCreateEvent(room)?.getSender?.() === currentUserId;
        const isRoomCreatedNearDraft = (room: Room, localRoom: (typeof localRooms)[number]) => {
            const createdAt = getRoomCreateEvent(room)?.getTs?.();
            if (!createdAt) return true;
            return Math.abs(createdAt - localRoom.createdAt) <= 60000;
        };

        const isLikelyRealRoomForDraft = (room: Room, localRoom: (typeof localRooms)[number]): boolean => {
            if (!isRoomCreatedNearDraft(room, localRoom)) return false;

            const roomOtherUserIds = getComparableOtherUserIds(
                room.getMembers()
                    .filter(member => {
                        const membership = member.membership;
                        return membership === 'join' || membership === 'invite';
                    })
                    .filter(member => member.userId !== currentUserId)
                    .map(member => member.userId)
            );
            const localOtherUserIds = getComparableOtherUserIds(
                localRoom.otherUsers.map(user => user.userId)
            );

            if (localRoom.isDirect && roomOtherUserIds.length > 1) return false;
            if (!localRoom.isDirect && roomOtherUserIds.length > localOtherUserIds.length) return false;
            if (!roomOtherUserIds.every(id => localOtherUserIds.includes(id))) return false;

            const localName = normalizeName(localRoom.name);
            const roomName = normalizeName(room.name);
            const createdByCurrentUser = isRoomCreatedByCurrentUser(room);
            if (!createdByCurrentUser) return false;

            if (localName) {
                if (isResolvedRoomName(roomName) && roomName !== localName) return false;
                return true;
            }

            return true;
        };

        const rooms = matrixService.getRooms().filter(room => {
            // Filter out rooms with phq_admin
            if (room.getJoinedMembers().some(member => member.rawDisplayName === "phq_admin")) {
                return false;
            }

            // Filter out rooms where user is not joined (kicked, left, banned, invited)
            const membership = room.getMyMembership();
            if (membership !== 'join') return false;

            // Filter out real rooms that have a corresponding local room (by ID mapping)
            if (hiddenRealRoomIds.has(room.roomId)) return false;

            // Hide real room candidates only when they match an active unmapped local draft.
            if (unmappedLocalRooms.some(localRoom => isLikelyRealRoomForDraft(room, localRoom))) {
                return false;
            }

            return true;
        });

        const directEvent = matrixService.getClient()?.getAccountData(EventType.Direct);
        // Undefined = account data not applied yet (sliding sync processes rooms first); the m.direct
        // AccountData event triggers another stateChange, which re-runs this self-heal.
        const accountDataLoaded = !!directEvent;
        const currentDirect = directEvent?.getContent() || {};

        for (const room of rooms) {
            const wasCreatedAsDirect = matrixService.wasRoomCreatedAsDirect(room.roomId);

            if (wasCreatedAsDirect && accountDataLoaded) {
                const roomInMDirect = Object.values(currentDirect).some((roomIds: string[]) =>
                    roomIds.includes(room.roomId)
                );

                if (!roomInMDirect) {
                    // chatState.currentUserId is null until setup finishes, and
                    // find() then returns ourselves — keying m.direct by our own id.
                    const selfUserId = matrixService.getCurrentUserMatrixId() || matrixService.getState().currentUserId;
                    const joinedMembers = room.getJoinedMembers();

                    if (!selfUserId) {
                        logger.warn('Skipping m.direct repair - own Matrix id is not known yet', {
                            source: 'chat',
                            event: 'm_direct_repair_skipped',
                            roomId: room.roomId,
                        });
                        continue;
                    }

                    const otherMember = joinedMembers.find(member => member.userId !== selfUserId);

                    if (otherMember && joinedMembers.length === 2) {
                        matrixService.addDirectRoomToAccountData(room.roomId, otherMember.userId).catch((error) => {
                            console.error('Failed to update m.direct account data:', error);
                        });
                    }
                }
            }
        }

        const roomsWithLastMsg: RoomWithLastMessage[] = rooms.map(room => {
            const lastMessage = getLastMessageForRoom(room);

            const allMembers = room.getJoinedMembers();
            const otherUsers = allMembers.filter(member => member.userId !== currentUserId && !isServiceParticipant(member.userId)).map(member => ({
                userId: member.userId,
                displayName: member.rawDisplayName || member.userId,
                avatarUrl: member.getAvatarUrl(window.__ENV__?.SYNAPSE_BASE_URL, 64, 64, 'crop', true, true) || '',
            }));

            let unreadCount = 0;
            try {
                unreadCount = matrixService.getUnreadMessageCount ? matrixService.getUnreadMessageCount(room.roomId) : 0
            } catch (error) {
                console.error('Error calculating unread count for room:', room.roomId, error);
                unreadCount = 0;
            }

            return {
                room,
                roomId: room.roomId,
                lastMessage,
                otherUsers,
                unreadCount,
                isLocal: false,
                isDirect: matrixService.isDirectMessageRoom(room)
            };
        });
        const localRoomsWithLastMsg: RoomWithLastMessage[] = localRooms.map(localRoom => {
            const mappedRealId = matrixService.getMappedRealRoomId(localRoom.localId);
            let unreadCount = 0;
            if (mappedRealId) {
                try {
                    unreadCount = matrixService.getUnreadMessageCount(mappedRealId);
                } catch (error) {
                    console.error('Error calculating unread count for room:', mappedRealId, error);
                }
            }
            return {
                room: null,
                roomId: mappedRealId || localRoom.localId,
                roomName: localRoom.name || localRoom.otherUsers.map(u => u.displayName).join(', ') || 'New chat',
                otherUsers: localRoom.otherUsers.map(u => ({
                    userId: u.userId,
                    displayName: u.displayName,
                    avatarUrl: u.avatarUrl || ''
                })),
                unreadCount,
                lastMessage: null,
                isLocal: true,
                isDirect: localRoom.isDirect,
                localCreatedAt: localRoom.createdAt
            };
        });

        const combinedRooms = [...roomsWithLastMsg, ...localRoomsWithLastMsg];

        combinedRooms.sort((a, b) => {
            const aTime = getRoomSortTimestamp(a.room, a.lastMessage?.timestamp, a.localCreatedAt);
            const bTime = getRoomSortTimestamp(b.room, b.lastMessage?.timestamp, b.localCreatedAt);
            return bTime - aTime;
        });

        setRoomsWithLastMessages(combinedRooms);
        return combinedRooms;
    }, [getLastMessageForRoom]);

    const markMessagesAsRead = useCallback(async (roomId: string, eventId?: string) => {
        await matrixService.markMessagesAsRead(roomId, eventId);
        setTimeout(() => updateRoomsWithLastMessages(), 100);
    }, [updateRoomsWithLastMessages]);

    const getAllUnreadCount = useCallback((): UnreadSummary => {
        const unreadCounts = summarizeUnreadRooms(roomsWithLastMessages);
        faviconBadge.update(unreadCounts.total);
        return unreadCounts;
    }, [roomsWithLastMessages]);

    const getRoomDisplayName = useCallback((room: Room): string => {
        if (room.name) return room.name;

        const allMembers = room.getJoinedMembers();
        const currentUserId = matrixService.getState().currentUserId;
        const otherUsers = allMembers.filter(member =>
            member.userId !== currentUserId && !isServiceParticipant(member.userId)
        );

        if (otherUsers.length === 1) {
            return otherUsers[0].rawDisplayName || otherUsers[0].userId;
        } else if (otherUsers.length > 1) {
            return otherUsers.map(user => user.rawDisplayName || user.userId).join(', ');
        }

        return 'Chat Room';
    }, []);

    const getLastMessageIdForRoom = useCallback((roomId: string): string | null => {
        try {
            const client = getClient();
            if (!client) return null;

            const room = client.getRoom(roomId);
            if (!room) return null;

            const timeline = room.getLiveTimeline();
            const events = timeline.getEvents();


            const messageEvents = events.filter(event => {
                if (event.getType() !== 'm.room.message' || event.isRedacted()) return false;
                const sender = event.getSender();
                if (sender && isBotUser(sender)) return false;
                const content = event.getContent();
                const msgtype = content?.msgtype;
                if (msgtype?.startsWith('org.phonehq.transcription_')
                    || msgtype?.startsWith('org.phonehq.summary_')
                    || isTranscriptionMessageContent(content)
                    || isSummaryMessageContent(content)) return false;
                return true;
            });

            if (messageEvents.length === 0) return null;

            const lastMessageEvent = messageEvents[messageEvents.length - 1];
            return lastMessageEvent.getId() || null;
        } catch (error) {
            console.error('Error getting last message ID for room:', roomId, error);
            return null;
        }
    }, []);


    const getLastMessageIdForSelectedRoom = useCallback((): string | null => {
        const selectedRoomId = state.selectedRoomId;
        if (!selectedRoomId) return null;

        return getLastMessageIdForRoom(selectedRoomId);
    }, [getLastMessageIdForRoom, state.selectedRoomId]);

    const shouldShowNotification = useCallback((message: MatrixEvent, room: Room): boolean => {
        const currentUserId = matrixService.getCurrentUserMatrixId();
        const sender = message.getSender();

        if (sender === currentUserId || sender === 'phq_admin') return false;

        // Check if room is muted
        if (isRoomChatNotificationMuted(room.roomId) || matrixService.isRoomMuted(room.roomId)) {
            return false;
        }

        const createEvent = room.currentState.getStateEvents('m.room.create', '');

        const roomType = createEvent?.getContent().type;

        const isSpace = roomType === 'm.space';
        if (isSpace) return false;

        const eventType = message.getType();
        if (eventType === 'm.room.member') {
            const content = message.getContent();

            if (isRoomMemberJoinEvent(message)) {
                const joinedUserId = message.getStateKey() || sender;
                if (joinedUserId && matrixService.consumeInitialInvitee(room.roomId, joinedUserId)) {
                    logDev('Skipping join notification - initial invitee', {
                        source: 'useMatrix',
                        roomId: room.roomId,
                        joinedUserId
                    });
                    return false;
                }

                if (matrixService.isRoomInSetup(room.roomId)) {
                    logDev('Skipping join notification - room in setup phase', {
                        source: 'useMatrix',
                        roomId: room.roomId,
                        sender
                    });
                    return false;
                }

                const roomAge = Date.now() - (createEvent?.getTs() || 0);
                const memberCount = room.getJoinedMemberCount();
                if (roomAge < 30000 && memberCount <= 3) {
                    logDev('Skipping join notification - new room with few members', {
                        source: 'useMatrix',
                        roomId: room.roomId,
                        roomAge,
                        memberCount
                    });
                    return false;
                }

                const displayName = content.displayname || sender;

                if (document.hasFocus()) {
                    dispatch(showToast({
                        title: `User joined the room`,
                        description:  `${displayName} joined the room ${getRoomDisplayName(room)}`,
                        type: 'info',
                        dontHide: false,
                        position: 'default',
                        icon: 'info',
                        navigateToRoom: room.roomId
                    }));
                    notificationAudioRef.play();
                    return false;
                }
                return true;
            }
            return false;
        }

        const content = message.getContent();
        const msgtype = content?.msgtype;

        if (sender && isBotUser(sender)) {
            const kind = isSummaryMessageContent(content) ? 'summary'
                : isTranscriptionMessageContent(content) ? 'transcription' : undefined;
            if (!kind || !currentUserId || !resultBelongsToCurrentRequest({
                result: message, events: room.getLiveTimeline().getEvents(), currentUserId, kind,
            })) return false;
            const metadata: unknown = content['org.phonehq.ai_result'];
            const resultId = record(metadata) && typeof metadata.result_id === 'string' ? metadata.result_id : message.getId();
            if (!resultId) return false;
            const notificationId = `${currentUserId}|${room.roomId}|${requestReference(content)}|${resultId}`;
            if (notifiedAiResultsRef.current.has(notificationId)) return false;
            notifiedAiResultsRef.current.add(notificationId);
            if (notifiedAiResultsRef.current.size > MAX_AI_NOTIFICATION_RESULTS) {
                const oldest = notifiedAiResultsRef.current.values().next().value;
                if (oldest) notifiedAiResultsRef.current.delete(oldest);
            }
            const roomName = getRoomDisplayName(room);
            if (document.hasFocus()) {
                dispatch(showToast({
                    title: kind === 'summary' ? 'AI summary ready' : 'AI transcript ready',
                    description: `in ${roomName}`,
                    type: 'info', dontHide: false, position: 'default', icon: 'info', navigateToRoom: room.roomId,
                }));
                notificationAudioRef.play();
            } else {
                browserNotificationService.showBotReadyNotification(kind === 'summary' ? 'summary' : 'transcript', roomName, room.roomId);
            }
            return false;
        }

        // Filter out remaining phonehq custom events from non-aiBot senders
        if (msgtype?.startsWith('org.phonehq.')) return false;

        if (document.hasFocus() && sender && eventType == 'm.room.message') {
            if (room.roomId === state.selectedRoomId && browserNavigationService.isOnChatPage()) {
                return false;
            }
            let description = formatMessagePreview(content.body || 'New message');

            if (content.msgtype === 'm.audio') {
                description = 'Voice message';
            } else if (content.msgtype === 'm.image') {
                description = 'Image';
            } else if (content.msgtype === 'm.video') {
                description = 'Video';
            } else if (content.msgtype === 'm.file') {
                description = `${content.filename || content.body || 'File'}`;
            }

            dispatch(showToast({
                title: `${(room.getMember(sender)?.name || sender)} - new message`,
                description,
                type: 'info',
                dontHide: false,
                position: 'default',
                icon: 'message-square-text',
                navigateToRoom: room.roomId
            }));
            notificationAudioRef.play();
            return false;
        }

        return true;
    }, [dispatch, getRoomDisplayName, state.selectedRoomId]);

    useEffect(() => {
        const handleWindowFocus = () => {
            const summary = notificationBatchingService.getPendingSummary();
            if (summary && (summary.count > 0 || summary.joinCount > 0)) {
                const isSingleRoom = summary.roomCount === 1;
                const roomText = isSingleRoom
                    ? summary.rooms[0]
                    : `${summary.roomCount} conversations`;

                let title = '';
                let description = '';

                if (summary.count > 0 && summary.joinCount > 0) {
                    title = `${summary.count} new message${summary.count > 1 ? 's' : ''} and ${summary.joinCount} user${summary.joinCount > 1 ? 's' : ''} joined`;
                    description = `in ${roomText}`;
                } else if (summary.count > 0) {
                    title = `You have ${summary.count} new message${summary.count > 1 ? 's' : ''}`;
                    description = `in ${roomText}`;
                } else if (summary.joinCount > 0) {
                    title = `${summary.joinCount} user${summary.joinCount > 1 ? 's' : ''} joined`;
                    description = `in ${roomText}`;
                }

                dispatch(showToast({
                    title,
                    description,
                    type: 'info',
                    dontHide: false,
                    position: 'default',
                    icon: 'message-square-text',
                    ...(isSingleRoom && summary.singleRoomId && { navigateToRoom: summary.singleRoomId })
                }));

                notificationBatchingService.clearPendingNotifications();
            }
        };

        window.addEventListener('focus', handleWindowFocus);

        return () => {
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [dispatch]);

    useEffect(() => ringOutcomes.subscribe(() => {
        updateRoomsWithLastMessages();
    }), [updateRoomsWithLastMessages]);

    useEffect(() => {
        const handleStateChange = (newState: ChatState) => {
            setState(newState);
            if (!newState.isAuthenticated) {
                setMessages(new Map());
                setRoomsWithLastMessages([]);
                isInitialSyncRef.current = true;
                notificationBatchingService.clearPendingNotifications();
                tabAnimator.stop();
                faviconBadge.clear();
                resetDirectRoomInFlight();
            }
            updateRoomsWithLastMessages();
        };

        const handleNewMessage = ({event, room}: { event: MatrixEvent; room: Room }) => {
            if (isMessageEdit(event)) {
                updateRoomsWithLastMessages();
                return;
            }

            const messageAge = Date.now() - event.getTs();
            const isRecentMessage = messageAge < 30000;
            const eventType = event.getType();

            setMessages(prev => {
                const roomMessages = prev.get(room.roomId) || [];
                const newMessages = new Map(prev);
                newMessages.set(room.roomId, [...roomMessages, event]);
                return newMessages;
            });

            updateRoomsWithLastMessages();

            if (isInitialSyncRef.current) return;
            const content = event.getContent();

            const isMediaMessage = content.msgtype && ['m.image', 'm.video', 'm.audio', 'm.file'].includes(content.msgtype) && (content.url || content.file?.url);
            const isTextMessage = content.msgtype && content.body && typeof content.body === 'string';
            const isMessageWithContent = isMediaMessage || isTextMessage;
            if (isRecentMessage && shouldShowNotification(event, room)) {
                const senderId = event.getSender();
                const senderMember = room.getMember(senderId || '');
                const senderDisplayName = senderMember?.rawDisplayName;

                const createEvent = room.currentState.getStateEvents('m.room.create', '');
                const roomType = createEvent?.getContent().type;
                const isSpace = roomType === 'm.space';
                if (isSpace) return;
                if (eventType === 'm.room.member') {
                    const content = event.getContent();
                    if (isRoomMemberJoinEvent(event)) {
                        const displayName = content.displayname || senderDisplayName || 'Someone';
                        tabAnimator.start(displayName);

                        notificationBatchingService.addJoinNotification(
                            room.roomId,
                            getRoomDisplayName(room),
                            displayName
                        );
                        notificationAudioRef.play();
                    }
                } else if (isMessageWithContent) {
                    // Show notification for any message with content (decrypted or plain text)
                    tabAnimator.start(senderDisplayName || 'Unknown');

                    notificationBatchingService.addMessageNotification(
                        event,
                        room,
                        senderDisplayName || 'Unknown'
                    );

                    notificationAudioRef.play();
                }
            }
        };

        const handleRoomListMessageUpdate = () => {
            updateRoomsWithLastMessages();
        };

        const handleNewRoom = () => {
            const currentState = matrixService.getState();
            setState({...currentState});
            updateRoomsWithLastMessages();
        };

        const handleRoomInvitation = (data: {
            roomId: string;
            inviterUserId: string;
            inviterName: string;
            roomName: string;
            timestamp: number;
        }) => {
            const client = getClient();
            const createEvent = client?.getRoom(data.roomId)?.currentState.getStateEvents('m.room.create', '');
            const roomType = createEvent?.getContent().type;
            const isSpace = roomType === 'm.space';

            if (isSpace) return;
            updateRoomsWithLastMessages();
            if(!isInitialized) return;
            browserNotificationService.showRoomInvitationNotification({
                roomId: data.roomId,
                inviterName: data.inviterName,
                roomName: data.roomName
            });
        };

        const handleAddedToRoom = () => {
            updateRoomsWithLastMessages();
        };

        const handleRoomJoined = () => {
            updateRoomsWithLastMessages();
        };




        const handleNotificationClick = async (roomId: string) => {
            if (!matrixService.getRoom(roomId)) return;

            matrixService.markExplicitRoomSelectionPending();
            browserNavigationService.navigateToChat(roomId);

            const delay = browserNavigationService.isOnChatPage() ? 0 : 200;
            setTimeout(() => {
                matrixService.selectRoom(roomId);
            }, delay);
        };

        browserNotificationService.setNotificationClickHandler(handleNotificationClick);

        // Register all event listeners
        matrixService.on('stateChange', handleStateChange);
        matrixService.on('newMessage', handleNewMessage);
        matrixService.on('messageUpdate', handleRoomListMessageUpdate);
        matrixService.on('newRoom', handleNewRoom);
        matrixService.on('addedToRoom', handleAddedToRoom);
        matrixService.on('roomInvitation', handleRoomInvitation);
        matrixService.on('roomJoined', handleRoomJoined);
        matrixService.on('localRoomsChange', handleNewRoom);

        setIsInitialized(true);
        const initialState = matrixService.getState();

        setState(initialState);
        updateRoomsWithLastMessages();

        setNotificationPermission(browserNotificationService.getPermissionStatus());

        return () => {
            matrixService.off('stateChange', handleStateChange);
            matrixService.off('newMessage', handleNewMessage);
            matrixService.off('messageUpdate', handleRoomListMessageUpdate);
            matrixService.off('newRoom', handleNewRoom);
            matrixService.off('roomInvitation', handleRoomInvitation);
            matrixService.off('addedToRoom', handleAddedToRoom);
            matrixService.off('roomJoined', handleRoomJoined);
            matrixService.off('localRoomsChange', handleNewRoom);
        };
    }, [updateRoomsWithLastMessages, shouldShowNotification, getRoomDisplayName]);

    const initialize = useCallback(async (authResponse: ChatLoginResponse) => {
        isInitialSyncRef.current = true;
        await matrixService.initialize(authResponse);
        setTimeout(() => {
            const unreadCounts = summarizeUnreadRooms(updateRoomsWithLastMessages());
            if (unreadCounts.total > 0) {
                logDev('User logged in with unread messages', {
                    source: 'useMatrix',
                    totalUnread: unreadCounts.total,
                    roomCount: unreadCounts.byRoom.length
                });

                if (unreadCounts.total === 1) {
                    const roomWithUnread = unreadCounts.byRoom[0];
                    const room = matrixService.getRoom(roomWithUnread.roomId);

                    if (room) {
                        const timeline = room.getLiveTimeline();
                        const events = timeline.getEvents();
                        const messageEvents = events.filter(event =>
                            event.getType() === 'm.room.message' &&
                            event.getSender() !== matrixService.getCurrentUserMatrixId()
                        );

                        if (messageEvents.length > 0) {
                            const lastMessage = messageEvents[messageEvents.length - 1];
                            const sender = lastMessage.getSender();
                            const senderMember = room.getMember(sender || '');
                            const senderDisplayName = senderMember?.rawDisplayName || sender?.split(':')[0].replace('@', '') || 'Unknown';
                            const messageContent = formatMessagePreview(lastMessage.getContent().body || 'New message');

                            dispatch(showToast({
                                title: `${senderDisplayName} - new message`,
                                description: messageContent.substring(0, 100),
                                type: 'info',
                                dontHide: false,
                                position: 'default',
                                icon: 'info',
                                navigateToRoom: room.roomId
                            }));

                            browserNotificationService.showMessageNotification(lastMessage, senderDisplayName, roomWithUnread.roomName);
                        }
                    }
                } else if (unreadCounts.total > 1) {
                    const isSingleConversation = unreadCounts.byRoom.length === 1;
                    const description = isSingleConversation
                        ? `in ${unreadCounts.byRoom[0].roomName}`
                        : `Across ${unreadCounts.byRoom.length} conversations`;

                    dispatch(showToast({
                        title: `You have ${unreadCounts.total} unread messages`,
                        description,
                        type: 'info',
                        dontHide: false,
                        position: 'default',
                        icon: 'info',
                        ...(isSingleConversation && { navigateToRoom: unreadCounts.byRoom[0].roomId })
                    }));

                    const notificationDescription = isSingleConversation
                        ? `in ${unreadCounts.byRoom[0].roomName}`
                        : `across ${unreadCounts.byRoom.length} conversations`;

                    browserNotificationService.showGroupedMessageNotification(
                        unreadCounts.total,
                        notificationDescription
                    );
                }
            }

            isInitialSyncRef.current = false;
        }, 1000);
    }, [updateRoomsWithLastMessages, dispatch]);

    const requestNotificationPermission = useCallback(async () => {
        const granted = await browserNotificationService.requestPermission();
        setNotificationPermission(browserNotificationService.getPermissionStatus());

        if (granted) {
            browserNotificationService.showSystemNotification('Notifications enabled for Chat');
        }

        return granted;
    }, []);

    const sendMessage = useCallback(async (roomId: string, message: string, mentions?: Array<{userId: string, displayName: string}>,customTag?: string, roomMentions?: Array<{roomId: string, name: string}>) => {
        await matrixService.sendMessage(roomId, message, mentions,customTag, roomMentions);
    }, []);

    const sendReplyMessage = useCallback(async (roomId: string, message: string, replyToEventId: string, mentions?: Array<{userId: string, displayName: string}>, roomMentions?: Array<{roomId: string, name: string}>) => {
        await matrixService.sendReplyMessage(roomId, message, replyToEventId, mentions, roomMentions);
    }, []);

    const sendPrivateReplyMessage = useCallback(async (roomId: string, message: string, source: {senderName: string; excerpt: string}, mentions?: Array<{userId: string, displayName: string}>, roomMentions?: Array<{roomId: string, name: string}>) => {
        await matrixService.sendPrivateReplyMessage(roomId, message, source, mentions, roomMentions);
    }, []);

    const sendEditMessage = useCallback(async (roomId: string, targetEventId: string, newText: string, mentions?: Array<{userId: string, displayName: string}>, roomMentions?: Array<{roomId: string, name: string}>) => {
        await matrixService.sendEditMessage(roomId, targetEventId, newText, mentions, roomMentions);
    }, []);

    const removeReaction = useCallback(async (roomId: string, eventId: string, emoji: string) => {
        await matrixService.removeReaction(roomId, eventId, emoji);
    }, []);

    const sendReaction = useCallback(async (roomId: string, eventId: string, emoji: string) => {
        await matrixService.sendReaction(roomId, eventId, emoji);
    }, []);

    const getClient = useCallback(() => {
        return matrixService.getClient();
    }, []);

    const getCurrentUserId = useCallback(() => {
        return matrixService.getState().currentUserId;
    }, []);

    const createRoom = useCallback(async (options: {
        name?: string;
        topic?: string;
        invite?: string[];
        isDirectMessage?: boolean;
    }, onInviteProgress?: (invited: number, total: number) => void) => {
        const parentRoom = findParentRoom();

        if (parentRoom) {
            const roomId = await matrixService.createRoomAsChild(options, parentRoom.roomId, onInviteProgress);
            return roomId;
        } else {
            console.warn('No parent room with phq_admin found, creating regular room');
            return await matrixService.createRoom(options, onInviteProgress);
        }
    }, [findParentRoom]);

    const changeRoomName = useCallback(async (roomId: string, newName: string) => {
        await matrixService.changeRoomName(roomId, newName);
    }, []);

    const changeRoomAvatar = useCallback(async (roomId: string, file: File) => {
        await matrixService.changeRoomAvatar(roomId, file);
    }, []);

    const kickUserFromRoom = useCallback(async (roomId: string, userId: string, reason?: string) => {
        await matrixService.kickUserFromRoom(roomId, userId, reason);
    }, []);
    const inviteUserToRoom = useCallback(async (roomId: string, userId: string) => {
        await matrixService.inviteUserToRoom(roomId, userId);
    }, []);
    const searchMessageBodies = useCallback(async (options: SearchOptions) => {
        return await matrixService.searchMessageBodies(options);
    }, []);
    const selectRoom = useCallback(async (roomId: string | null) => {
        if (!roomId) {
            matrixService.selectRoom(null);
            return;
        }

        let room = matrixService.getRoom(roomId);

        if (!room) {
            room = await matrixService.waitForRoom(roomId, 3000);

            if (!room) {
                console.error('Room not found after waiting:', roomId);
            }
        }

        matrixService.selectRoom(roomId);
    }, []);

    const sendFile = useCallback(async (roomId: string, file: File, replyToEventId?: string, caption?: string, batchId?: string, mentions?: Array<{userId: string, displayName: string}>, privateReplySource?: {senderName: string; excerpt: string}) => {
        await matrixService.sendFile(roomId, file, replyToEventId, caption, batchId, mentions, privateReplySource);
    }, []);

    const getRoomMessages = useCallback((roomId: string): MatrixEvent[] => {
        return messages.get(roomId) || [];
    }, [messages]);

    const logout = useCallback(async () => {
        await matrixService.logout();
    }, []);
    const startCall = useCallback(async (userId: string) => {
        await matrixService.startCall(userId);
    }, []);

    const setSessionLockState = useCallback((sessionLockState: ChatState['sessionLockState']) => {
        matrixService.setSessionLockState(sessionLockState);
    }, []);

    const stopForSessionLockLoss = useCallback(async () => {
        await matrixService.stopForSessionLockLoss();
    }, []);

    const requestSessionTakeover = useCallback(() => {
        matrixService.requestSessionTakeover();
    }, []);

    const isRoomEncrypted = useCallback((roomId: string): boolean => {
        const room = matrixService.getRoom(roomId);
        if (!room) {
            return false;
        }
        return room.hasEncryptionStateEvent();
    }, []);

    const enableRoomEncryption = useCallback(async (roomId: string): Promise<void> => {
        await matrixService.enableRoomEncryption(roomId);
    }, []);

    const loadMoreRooms = useCallback(async (count: number = 50): Promise<void> => {
        await matrixService.loadMoreRooms(count);
    }, []);

    const hasMoreRooms = useMemo(() => {
        const totalRoomCount = matrixService.getTotalRoomCount();
        return totalRoomCount > 0 && state.rooms.length < totalRoomCount;
    }, [state.rooms.length]);

    return {
        isInitialized: state.isInitialized,
        isAuthenticated: state.isAuthenticated,
        hasCompletedInitialSync: state.hasCompletedInitialSync,
        initialRoomRestoreSettled: state.initialRoomRestoreSettled,
        sessionLockState: state.sessionLockState,
        currentUserId: state.currentUserId,
        selectedRoomId: state.selectedRoomId,
        rooms: state.rooms,
        roomsWithLastMessages,
        syncState: state.syncState,
        error: state.error,
        notificationPermission,

        initialize,
        getCurrentUserId,
        sendMessage,
        sendReplyMessage,
        sendPrivateReplyMessage,
        sendEditMessage,
        sendReaction,
        removeReaction,
        createRoom,
        selectRoom,
        sendFile,
        getClient,
        logout,
        setSessionLockState,
        stopForSessionLockLoss,
        requestSessionTakeover,
        requestNotificationPermission,
        markMessagesAsRead,
        getRoomMessages,
        updateRoomsWithLastMessages,
        findParentRoom,
        changeRoomName,
        changeRoomAvatar,
        kickUserFromRoom,
        inviteUserToRoom,
        searchMessageBodies,
        loadMoreRooms,
        hasMoreRooms,
        getAllUnreadCount,
        startCall,
        getLastMessageIdForSelectedRoom,
        isRoomEncrypted,
        enableRoomEncryption,
        tabAnimator
    };
};

const isSameDate = (date1: Date, date2: Date): boolean => {
    return date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear();
};

export const formatLastMessageTime = (timestamp: number): string => {
    const now = new Date();
    const messageDate = new Date(timestamp);
    const diffMs = now.getTime() - messageDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 2) {
        return 'Now';
    }

    if (isSameDate(messageDate, now)) {
        const options: Intl.DateTimeFormatOptions = {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        };
        return messageDate.toLocaleTimeString('en-US', options);
    }

    const daysBetween = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (daysBetween <= 6) {
        return weekDays[messageDate.getDay()].substring(0, 3);
    }

    const currentYear = now.getFullYear();
    const messageYear = messageDate.getFullYear();

    if (messageYear === currentYear) {
        return messageDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    } else {
        return formatDateDayMonth(messageDate);
    }
};

export const truncateMessage = (message: string, maxLength: number = 40): string => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength - 3) + '...';
};

export interface MessageGroup {
    date: string;
    displayDate: string;
    messages: MatrixEvent[];
}


const groupMessagesByDay = (messages: MatrixEvent[], orderIndex?: Map<string, number>): MessageGroup[] => {
    const groups = new Map<string, MatrixEvent[]>();

    messages.forEach(message => {
        const timestamp = message.getTs();
        const date = new Date(timestamp);
        const dateKey = date.toISOString().split('T')[0];

        if (!groups.has(dateKey)) {
            groups.set(dateKey, []);
        }
        groups.get(dateKey)!.push(message);
    });

    const compare = (a: MatrixEvent, b: MatrixEvent): number => {
        const ia = orderIndex?.get(a.getId() ?? '');
        const ib = orderIndex?.get(b.getId() ?? '');
        if (ia !== undefined && ib !== undefined) return ia - ib;
        return a.getTs() - b.getTs();
    };

    const sortedGroups = Array.from(groups.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateKey, msgs]) => ({
            date: dateKey,
            displayDate: formatDisplayDate(dateKey),
            messages: msgs.sort(compare)
        }));

    return sortedGroups;
};

const formatDisplayDate = (dateKey: string): string => {
    const date = new Date(dateKey + 'T00:00:00');
    const today = new Date();
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);

    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    const currentYear = today.getFullYear();
    const messageYear = date.getFullYear();

    if (messageYear === currentYear) {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
        });
    } else {
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    }
};

export const useRoomMessages = (roomId: string | null) => {
    const [messages, setMessages] = useState<MatrixEvent[]>([]);
    // Bumped by every leg of a call. A call's answer and hangup never become
    // messages, so nothing else in this hook changes when one lands — yet they
    // are exactly what turns a ringing row into "Missed call".
    const [callRevision, setCallRevision] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const prevGroupedRef = useRef<MessageGroup[]>([]);

    // Filter out system messages that appear before the first real message
    const filteredMessages = useMemo(() => {
        if (messages.length === 0) return [];

        const isLeadingSystemEvent = (msg: MatrixEvent) => {
            const type = msg.getType();
            return type === 'm.room.member' || type === 'm.room.name' || type === 'm.room.topic';
        };

        const withoutNoise = keepMembershipTransitions(messages);

        const withoutDuplicateEvents = deduplicateTimelineEvents(withoutNoise);
        const firstRealIndex = withoutDuplicateEvents.findIndex(msg => !isLeadingSystemEvent(msg));
        if (firstRealIndex <= 0) return withoutDuplicateEvents;

        return withoutDuplicateEvents.slice(firstRealIndex);
    }, [messages]);

    const groupedMessages = useMemo(() => {
        if (filteredMessages.length === 0) {
            prevGroupedRef.current = [];
            return [];
        }

        // Order by the SDK live timeline (canonical stream order) instead of
        // origin_server_ts, so incoming bursts/out-of-order decryptions render
        // in the same order the server stored them.
        const orderIndex = new Map<string, number>();
        const liveEvents = roomId
            ? matrixService.getRoom(roomId)?.getLiveTimeline().getEvents() ?? []
            : [];
        liveEvents.forEach((ev, i) => {
            const id = ev.getId();
            if (id) orderIndex.set(id, i);
        });

        const newGrouped = groupMessagesByDay(filteredMessages, orderIndex);
        const prevGrouped = prevGroupedRef.current;

        const optimizedGroups = newGrouped.map(newGroup => {
            const existingGroup = prevGrouped.find(g => g.date === newGroup.date);

            const sameMessage = (a: MatrixEvent, b: MatrixEvent | undefined) =>
                !!b && a.getId() === b.getId() && a.replacingEventId() === b.replacingEventId();

            if (existingGroup) {
                const messagesIdentical =
                    existingGroup.messages.length === newGroup.messages.length &&
                    existingGroup.messages.every((msg, i) => sameMessage(msg, newGroup.messages[i]));

                if (messagesIdentical) {
                    return existingGroup;
                }

                const oldMessagesUnchanged = existingGroup.messages.every((msg, i) =>
                    sameMessage(msg, newGroup.messages[i])
                );

                if (oldMessagesUnchanged && newGroup.messages.length > existingGroup.messages.length) {
                    return {
                        ...existingGroup,
                        messages: newGroup.messages
                    };
                }
            }

            return newGroup;
        });

        prevGroupedRef.current = optimizedGroups;
        return optimizedGroups;
    }, [messages, roomId]);

    useEffect(() => {

        if (!roomId) {
            setMessages([]);
            return;
        }
        let cancelled = false;
        const isStale = () => cancelled;
        const loadMessages = async () => {
            setLoading(true);

            try {
                const cachedMessages = matrixService.getRoomMessages(roomId, 50);
                if (cachedMessages.length > 0) {
                    setMessages(cachedMessages);
                    setLoading(false);
                }

                const room = matrixService.getRoom(roomId);
                if (room) {
                    const timeline = room.getLiveTimeline();
                    const currentEventCount = timeline?.getEvents().length || 0;

                    if (currentEventCount < 80 && timeline?.getPaginationToken(Direction.Backward)) {
                        await matrixService.loadOlderMessages(roomId, 100);
                    }
                }

                if (isStale()) return;
                const roomMessages = matrixService.getRoomMessages(roomId, 50);
                setMessages(roomMessages);
            } catch (error) {
                if (isStale()) return;
                setMessages([]);
            } finally {
                if (!isStale()) setLoading(false);
            }
        };

        loadMessages();

        const handleNewMessage = ({ event, room: messageRoom }: { event: MatrixEvent; room: Room }) => {
            if (messageRoom.roomId === roomId) {
                const eventType = event.getType();
                const content = event.getContent();

                // Hide custom phonehq events from timeline
                if (
                    content.msgtype?.startsWith('org.phonehq.transcription_')
                    || content.msgtype?.startsWith('org.phonehq.summary_')
                    || isTranscriptionMessageContent(content)
                    || isSummaryMessageContent(content)
                ) {
                    return;
                }

                // Hide replacement events (edits/deletions) from being added as separate messages
                if (isMessageEdit(event)) {
                    return;
                }

                // The invite is the call's row in the conversation; every other
                // leg is plumbing and is folded into it instead.
                const isCallRow = isCallInviteEvent(event) || isRtcNotificationEvent(event);
                const isSystemMessage = eventType === 'm.room.member' || eventType === 'm.room.name';
                const isMediaMessage = content.msgtype && ['m.image', 'm.video', 'm.audio', 'm.file'].includes(content.msgtype) && (content.url || content.file?.url);
                const isTextMessage = content.msgtype && content.body && typeof content.body === 'string';
                const isMessageWithContent = isMediaMessage || isTextMessage || isSystemMessage || isCallRow;

                if (!isMessageWithContent) {
                    return;
                }

                setMessages(prev => {
                    const existingIds = new Set(prev.map(msg => msg.getId()));

                    if (existingIds.has(event.getId())) {
                        return prev;
                    }

                    return [...prev, event];
                });
            }
        };

        const handleMessageUpdate = ({ event, room: messageRoom }: { event: MatrixEvent; room: Room }) => {
            if (messageRoom.roomId === roomId) {
                if (isMessageEdit(event)) return;
                if (isRtcSignalEvent(event)) {
                    setMessages(prev => prev.filter(msg => msg.getId() !== event.getId()));
                    return;
                }

                const content = event.getContent();

                if (
                    content && (
                        content.msgtype?.startsWith('org.phonehq.transcription_')
                        || content.msgtype?.startsWith('org.phonehq.summary_')
                        || isTranscriptionMessageContent(content)
                        || isSummaryMessageContent(content)
                    )
                ) {
                    // If a hidden event slipped into state before decryption/filtering, remove it.
                    setMessages(prev => prev.filter(msg => msg.getId() !== event.getId()));
                    return;
                }

                setMessages(prev => {
                    const messageIndex = prev.findIndex(msg => msg.getId() === event.getId());

                    if (messageIndex === -1) {
                        if (event.replacingEventId()) return prev;
                        // Message not in state yet, add it
                        return [...prev, event];
                    }

                    // Force array recreation to trigger React re-render
                    const updatedMessages = [...prev];
                    updatedMessages[messageIndex] = event;
                    return updatedMessages;
                });
            }
        };

        const handleMessageDelete = ({ eventId, roomId: deleteRoomId }: { eventId: string; roomId: string }) => {
            if (deleteRoomId === roomId) {
                setMessages(prev => {
                    const index = prev.findIndex(msg => msg.getId() === eventId);
                    if (index === -1) return prev;
                    return [...prev];
                });
            }
        };

        const handleTimelineReset = ({ roomId: resetRoomId }: { roomId: string }) => {
            if (resetRoomId === roomId) {
                const roomMessages = matrixService.getRoomMessages(roomId, 50);
                setMessages(roomMessages);
            }
        };

        const handleCallEvent = ({ event, room: callRoom }: { event: MatrixEvent; room: Room }) => {
            if (callRoom.roomId !== roomId || !isCallTimelineEvent(event)) return;
            if (isCallInviteEvent(event)) handleNewMessage({ event, room: callRoom });
            setCallRevision(revision => revision + 1);
        };

        // Subscribe to events
        matrixService.on('newMessage', handleNewMessage);
        matrixService.on('messageUpdate', handleMessageUpdate);
        matrixService.on('messageDelete', handleMessageDelete);
        matrixService.on('timelineReset', handleTimelineReset);
        matrixService.on('callEvent', handleCallEvent);

        return () => {
            cancelled = true;
            matrixService.off('newMessage', handleNewMessage);
            matrixService.off('messageUpdate', handleMessageUpdate);
            matrixService.off('messageDelete', handleMessageDelete);
            matrixService.off('timelineReset', handleTimelineReset);
            matrixService.off('callEvent', handleCallEvent);
        };
    }, [roomId]);

    /**
     * One entry per call the room knows about, keyed by call id and by the
     * invite's event id. Folded from the live timeline rather than from
     * `messages`: only the invite is a row, and the events that decide what
     * that row says are not.
     */
    const callEntries = useMemo(() => {
        // Triggers, not inputs — see above.
        void messages;
        void callRevision;
        return roomId
            ? matrixService.getRoomCallEntries(roomId)
            : new Map<string, CallTimelineEntry>();
    }, [roomId, messages, callRevision]);

    const refreshMessages = () => {
        if (roomId) {
            setLoading(true);
            try {
                const roomMessages = matrixService.getRoomMessages(roomId, 50);
                setMessages(roomMessages);
            } catch (error) {
                console.error('Error refreshing messages:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const loadMoreMessages = async (count: number = 30) => {
        if (!roomId || isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);

        try {
            const result = await matrixService.loadOlderMessages(roomId, count);
            setMessages(result.messages);
            setHasMore(result.hasMore);
        } catch (error) {
            console.error('Error loading more messages:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        setHasMore(true);
    }, [roomId]);

    return {
        messages,
        groupedMessages,
        callEntries,
        loading,
        isLoadingMore,
        hasMore,
        refreshMessages,
        loadMoreMessages
    };
};

export const useRoomActions = (roomId: string | null) => {
    const tenant = useSelector(selectTenant);
    const userId = useSelector(selectUserId);
    const shouldLoadMutes = Boolean(tenant && userId);
    const {currentData: activeMutes, refetch: refetchActiveMutes} = useGetActiveChatNotificationMutesQuery(
        {
            tenant: tenant ?? '',
            userId: userId ?? '',
        },
        {skip: !shouldLoadMutes},
    );
    const [muteChatRoom] = useMuteChatRoomMutation();
    const [unmuteChatRoom] = useUnmuteChatRoomMutation();
    const [roomMute, setRoomMute] = useState<ChatNotificationMute | null>(null);

    useEffect(() => {
        if (!roomId) {
            setRoomMute(null);
            return;
        }

        const backendMute = activeMutes?.find((mute) => mute.scope === 'ROOM' && mute.roomId === roomId);
        setRoomMute(
            backendMute
                ?? (matrixService.isRoomMuted(roomId)
                    ? {scope: 'ROOM', roomId, mutedUntil: null}
                    : null),
        );
    }, [activeMutes, roomId]);

    useEffect(() => {
        if (!roomMute?.mutedUntil || !roomId) return;

        const expiresInMs = Date.parse(roomMute.mutedUntil) - Date.now();
        if (expiresInMs <= 0) {
            setRoomMute(null);
            removeActiveRoomChatNotificationMute(roomId);
            void refetchActiveMutes();
            return;
        }

        const expiryTimer = window.setTimeout(() => {
            setRoomMute(null);
            removeActiveRoomChatNotificationMute(roomId);
            void refetchActiveMutes();
        }, expiresInMs);

        return () => window.clearTimeout(expiryTimer);
    }, [refetchActiveMutes, roomId, roomMute]);

    const muteRoom = useCallback(async (durationHours: ChatNotificationMuteDurationHours | null) => {
        if (!roomId) return;

        try {
            if (durationHours === null) {
                await matrixService.setRoomMuted(roomId, true);
                const permanentMute: ChatNotificationMute = {scope: 'ROOM', roomId, mutedUntil: null};
                setRoomMute(permanentMute);
                setActiveRoomChatNotificationMute(permanentMute);
                return;
            }

            const mute = await muteChatRoom({roomId, durationHours}).unwrap();
            setRoomMute(mute);
            setActiveRoomChatNotificationMute(mute);
        } catch (error) {
            console.error('Failed to mute room:', error);
            throw error;
        }
    }, [muteChatRoom, roomId]);

    const unmuteRoom = useCallback(async () => {
        if (!roomId) return;

        try {
            const results = await Promise.allSettled([
                unmuteChatRoom(roomId).unwrap(),
                matrixService.setRoomMuted(roomId, false),
            ]);
            const failure = results.find((result) => result.status === 'rejected');
            if (failure?.status === 'rejected') throw failure.reason;

            setRoomMute(null);
            removeActiveRoomChatNotificationMute(roomId);
        } catch (error) {
            console.error('Failed to unmute room:', error);
            throw error;
        }
    }, [roomId, unmuteChatRoom]);

    const leaveRoom = useCallback(async (options?: {forget?: boolean}) => {
        if (!roomId) return;

        try {
            await matrixService.leaveRoom(roomId, options);
        } catch (error) {
            console.error('Failed to leave room:', error);
            throw error;
        }
    }, [roomId]);

    return {
        isMuted: (roomMute ? isChatNotificationMuteActive(roomMute) : false)
            || (roomId ? matrixService.isRoomMuted(roomId) : false),
        muteRoom,
        unmuteRoom,
        leaveRoom
    };
};

export const useTyping = (roomId: string | null): string[] => {
    const [typingUsers, setTypingUsers] = useState<string[]>([]);
    const currentUserId = matrixService.getState().currentUserId;

    useEffect(() => {
        if (!roomId) return;

        const client: MatrixClient | null = matrixService.getClient();
        if (!client) return;

        const handleMemberTyping = (event: MatrixEvent, member: RoomMember): void => {

            if (member.roomId === roomId) {
                const room: Room | null = client.getRoom(roomId);
                if (room) {
                        const typingContent = event.getContent();
                        const typingUserIds: string[] = typingContent.user_ids.filter((id: string) => id !== currentUserId) || [];
                        setTypingUsers(typingUserIds);
                    } else {
                        setTypingUsers([]);
                    }

            }
        };

        client.on(RoomMemberEvent.Typing, handleMemberTyping);
        const room: Room | null = client.getRoom(roomId);
        if (room) {
            const typingEvent: MatrixEvent | null = room.currentState.getStateEvents('m.typing', '');
            if (typingEvent) {
                const typingContent = typingEvent.getContent();
                const typingUserIds: string[] = typingContent.user_ids.filter((id: string) => id !== currentUserId) || [];
                setTypingUsers(typingUserIds);
            } else {
                setTypingUsers([]);
            }
        }

        return () => {
            client.off(RoomMemberEvent.Typing, handleMemberTyping);
        };
    }, [roomId]);
    return typingUsers;
};

export {groupMessagesByDay, formatDisplayDate};
